import { useState, type ReactNode } from 'react';
import { Archive, RotateCcw } from 'lucide-react';
import { Button, ConfirmDialog, StatusBadge, cn, useToast } from '@dru/ui';
import { errorMessage } from '../../../lib/auth';

/**
 * Taking a catalogue entry out of use, and putting it back.
 *
 * Three things were wrong with the bare red word this replaces, and they are
 * the same three thing every time an action is written as text:
 *
 *   · it fired on the first click, with nothing in between. Retiring a test
 *     changes what every protocol in the country can ask for, and a
 *     mis-click on the row below the one you meant is indistinguishable from
 *     a decision;
 *   · it did not look like a control, so on a table of twelve rows the only
 *     way to find it was to read every line;
 *   · and when it worked, nothing happened on screen except a number
 *     changing somewhere — so people clicked it twice.
 *
 * The confirmation names what is affected — "used by 4 live protocols" — and
 * what changes for workers from now on. The masters are the source of truth
 * for what a worker is tested for, so "are you sure?" is not enough: the
 * person retiring an entry has to be told whose examinations it changes.
 *
 * The state a row is in is shown beside it rather than inferred from which
 * word the action offers, because "this row says Reactivate, therefore it
 * must be off" is a deduction nobody should have to make about their own
 * catalogue.
 */
export function RetireAction({
  active, name, what, onConfirm, disabled, disabledReason, impact, consequence, variant = 'compact',
}: {
  active: boolean;
  /** The row's own name, so the question names the thing and not "this item". */
  name: string;
  /** What kind of row it is, lower case: "test", "hazard", "package". */
  what: string;
  onConfirm: () => Promise<unknown>;
  /** In use somewhere that makes retiring it wrong. */
  disabled?: boolean;
  disabledReason?: ReactNode;
  /**
   * Where it is used, in numbers: "Used by 4 live protocols and 2 packages."
   * Shown in the confirmation's "affects" box.
   */
  impact?: ReactNode;
  /**
   * What retiring it changes from now on, for this kind of entry. Defaults to
   * a sentence that is true of every catalogue entry.
   */
  consequence?: ReactNode;
  /** `compact` for a table row; `button` for a detail page's header. */
  variant?: 'compact' | 'button';
}) {
  const { toast } = useToast();
  const [asking, setAsking] = useState(false);

  // `ConfirmDialog` drives its own busy state off this promise, so throwing
  // keeps the dialog open with the error rather than closing on a failure.
  async function go() {
    try {
      await onConfirm();
      setAsking(false);
      toast('success', active
        ? `${name} is retired. Every record that already used it keeps it.`
        : `${name} is back in use.`);
    } catch (e) {
      toast('danger', errorMessage(e));
      throw e;
    }
  }

  const label = active ? 'Retire' : 'Reactivate';
  const icon = active
    ? <Archive className="h-3.5 w-3.5" aria-hidden />
    : <RotateCcw className="h-3.5 w-3.5" aria-hidden />;
  const title = disabled && disabledReason
    ? String(disabledReason)
    : active ? `Retire ${name}` : `Put ${name} back in use`;

  return (
    <>
      {variant === 'button' ? (
        <Button
          variant="secondary"
          onClick={() => setAsking(true)}
          disabled={disabled}
          title={title}
          icon={icon}
          className={active ? 'border-danger-600/40 text-danger-600 hover:bg-danger-100' : undefined}
        >
          {label}
        </Button>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (!disabled) setAsking(true); }}
          disabled={disabled}
          title={title}
          aria-label={`${label} ${name}`}
          className={cn(
            'inline-flex min-h-[28px] max-md:min-h-10 items-center gap-1.5 rounded-btn border px-2 py-1 text-caption font-medium',
            'transition-colors duration-hover focus:outline-none focus:ring-4 focus:ring-primary-500/15',
            disabled
              ? 'cursor-not-allowed border-neutral-200 text-neutral-400'
              : active
                ? 'border-danger-600/30 text-danger-600 hover:bg-danger-100'
                : 'border-primary-600/30 text-primary-700 hover:bg-primary-50',
          )}
        >
          {icon} {label}
        </button>
      )}

      {asking && (
        <ConfirmDialog
          title={active ? `Retire ${name}?` : `Put ${name} back in use?`}
          what={active
            ? (consequence ?? `Nothing new can use this ${what} from now on.`)
            : `This ${what} becomes available again, and anything that still refers to it picks it up for new work.`}
          affects={active ? (
            <>
              {impact && <p className="font-medium text-neutral-900">{impact}</p>}
              <p className={impact ? 'mt-1' : undefined}>
                Nothing already recorded changes: examinations, results and certificates that used
                it keep it. The catalogue retires rather than deletes, so this can be reversed.
              </p>
            </>
          ) : impact}
          confirmLabel={active ? `Retire ${what}` : 'Reactivate'}
          tone={active ? 'danger' : 'primary'}
          onCancel={() => setAsking(false)}
          onConfirm={go}
        />
      )}
    </>
  );
}

/** Live or retired, said plainly rather than deduced from the action's wording. */
export function CatalogueState({ active }: { active: boolean }) {
  return (
    <StatusBadge tone={active ? 'covered' : 'neutral'}>
      {active ? 'In use' : 'Retired'}
    </StatusBadge>
  );
}

/** "1 protocol", "3 protocols". */
export function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}
